import { useState, useEffect, useCallback } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { LambdaListEditor } from 'config-editor';
import { LambdaGroup, LambdaDefaults } from 'config-editor-common';
import { parseJsonField } from '../../utils';

interface UiOptions {
  isAuthorizer?: boolean;
}

type Props = FieldExtensionComponentProps<LambdaGroup, UiOptions>;

/**
 * Scaffolder field that edits the full phx-api-v2 lambda group structure:
 * { defaults, iam_policy_names, iam_policy_names_custom, lambdas }
 */
export const LambdaGroupEditor = (props: Props) => {
  const { onChange, schema, uiSchema, formData } = props;
  const title = schema.title || uiSchema?.['ui:title'] || 'Lambda Configuration';
  const description =
    schema.description ||
    uiSchema?.['ui:description'] ||
    'Configure defaults, IAM policies, and lambdas';
  const isAuthorizer = uiSchema?.['ui:options']?.isAuthorizer ?? false;

  const getInitialValue = useCallback((): LambdaGroup => {
    if (formData === undefined || formData === null) {
      return {
        defaults: undefined,
        iam_policy_names: [],
        iam_policy_names_custom: [],
        lambdas: {},
      };
    }
    const parsed = parseJsonField(formData, {} as LambdaGroup);
    return {
      defaults: parsed.defaults,
      iam_policy_names: parsed.iam_policy_names ?? [],
      iam_policy_names_custom: parsed.iam_policy_names_custom ?? [],
      lambdas: parsed.lambdas ?? {},
    };
  }, [formData]);

  const [group, setGroup] = useState<LambdaGroup>(() => getInitialValue());

  useEffect(() => {
    setGroup(getInitialValue());
  }, [getInitialValue]);

  const updateGroup = useCallback(
    (updates: Partial<LambdaGroup>) => {
      const newGroup: LambdaGroup = {
        ...group,
        ...updates,
      };
      setGroup(newGroup);
      onChange(newGroup);
    },
    [group, onChange],
  );

  return (
    <LambdaListEditor
      lambdas={group.lambdas ?? {}}
      defaults={group.defaults}
      iamPolicyNames={group.iam_policy_names ?? []}
      customIamPolicyNames={group.iam_policy_names_custom ?? []}
      onIamPolicyNamesChange={policies => updateGroup({ iam_policy_names: policies })}
      onCustomIamPolicyNamesChange={policies => updateGroup({ iam_policy_names_custom: policies })}
      onDefaultsChange={(defaults: LambdaDefaults) => updateGroup({ defaults })}
      onChange={lambdas => updateGroup({ lambdas })}
      isAuthorizer={isAuthorizer}
      title={title}
      description={description}
      showPaper
    />
  );
};
